import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader, ScrollableContainer, StyledPageWrapper, Title } from "@monorepo/components";
import { CHARITY } from "@monorepo/constants";
import { routeConfig } from "@monorepo/constants/routeConfig";
import { CharityIcon } from "@monorepo/icons";
import { useServicesQuery } from "@monorepo/services/api/services";
import theme from "@monorepo/styles/theme";
import { IService } from "@monorepo/types";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import { createSelector } from "@reduxjs/toolkit";
import { reverse } from "named-urls";

import FilteredServicesList from "components/Services/FilteredServicesList/FilteredServicesList";
import { PageWrapper } from "components/Services/ServicesPage";

const CharityServices: React.FC = () => {
  const { t } = useTranslation();

  const filterServicesByCharityKeyword = useMemo(() => {
    const emptyArray = [] as Array<{}>;
    // https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results
    return createSelector(
      (res: any) => res.data,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      (_: any, CHARITY: string) => CHARITY,
      // eslint-disable-next-line @typescript-eslint/no-shadow
      (data: Array<IService>, CHARITY: string) =>
        (data?.filter(({ keywords }) => keywords.toLowerCase().includes(CHARITY.toLowerCase())) as Array<IService>) ??
        (emptyArray as Array<{}>)
    );
  }, []);

  const { charityServices, isServicesLoading } = useServicesQuery(undefined as void, {
    selectFromResult: (result) => ({
      charityServices: filterServicesByCharityKeyword(result, CHARITY),
      isServicesLoading: result.isLoading,
    }),
  });

  if (isServicesLoading) {
    return <Loader />;
  }

  return (
    <StyledPageWrapper>
      <PageWrapper>
        <Title>{t("services.easy_to_do_good_deeds")}</Title>
        <Box sx={{ position: "relative", height: "200px", width: "200px" }}>
          <CharityIcon sx={{ width: "100%", height: "100%" }} />
        </Box>
        <ScrollableContainer>
          <Typography sx={{ mb: theme.spacing(2) }} textAlign="justify">
            {t("charityServices.info")}
          </Typography>
          <FilteredServicesList
            services={charityServices}
            pathToPush={(charityId) =>
              reverse(routeConfig.dashboard.services.charityServices.charityId, {
                charityId: charityId,
              })
            }
          />
        </ScrollableContainer>
      </PageWrapper>
    </StyledPageWrapper>
  );
};

export default CharityServices;
